import { Form, Input, Select } from "antd";
import { message } from "utils/ciq-message";
import { useContext, useMemo } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import dayjs from "utils/dayjs-config";
import { DatePicker } from "components/antd-date-picker";
import { useShippingLocation } from "hooks/material";
import { LocationSearchNotFoundMessages } from "components/widgets/location-search-not-found-messages";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { gql } from "@apollo/client";
import { UPDATE_FEATURE_INSTANCES } from "entity-app/graphQL/ciq-feature-mutations";
import { useProjectParticipants } from "hooks/project-participants";
import { useEnumValues } from "hooks/enum-values";
import { FeatureTypes } from "entity-app/constants";

const { Option } = Select;

type CustomFieldColumn = {
  headerName: string;
  field: string;
  field_type: string;
};

function CustomFields(
  props: Readonly<{
    featureInstanceData: any;
    type: FeatureTypes;
    columns: CustomFieldColumn[];
    onChange?: (fieldName: string, value: any) => void;
    className?: string;
    disabled?: boolean;
  }>
) {
  const { featureInstanceData, type, columns, onChange, className, disabled } =
    props;
  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);

  const customFields = useMemo(() => {
    if (featureInstanceData?._custom_fields) {
      return featureInstanceData._custom_fields;
    }

    return columns.reduce(
      (acc, col) => {
        const fieldKey = col.field.replace("_custom_fields.", "");
        acc[fieldKey] = null;
        return acc;
      },
      {} as Record<string, null>
    );
  }, [featureInstanceData?._custom_fields, columns]);

  const { projectParticipants } = useProjectParticipants();
  const { enumOptionsMap } = useEnumValues(props.type);

  const {
    searchLocation,
    fetching,
    selectOptions: shippingLocationOptionResults,
    searchedString,
    clearOptions
  } = useShippingLocation({
    gqlClientForProject,
    projectToken: tokenRetrievalState?.token
  });

  const vendorOptions = useMemo(() => {
    return projectParticipants?.materialTradePartners?.map((company: any) => {
      return {
        id: company.vendor_id,
        name: company.subscription_vendor.name
      };
    });
  }, [projectParticipants?.materialTradePartners]);

  const userOptions = useMemo(() => {
    const allUsers = [
      ...(projectParticipants?.gcReviewers || []),
      ...(projectParticipants?.designReviewers || []),
      ...(projectParticipants?.submitterUsers || [])
    ];

    const uniqueUsers = allUsers.reduce((acc: any[], user: any) => {
      if (user.type !== "poc" && !acc.find((u: any) => u.id === user.id)) {
        acc.push(user);
      }
      return acc;
    }, []);

    return uniqueUsers.map((user: any) => ({
      id: user.id,
      name: `${user.first_name || ""} ${user.last_name || ""}`.trim()
    }));
  }, [
    projectParticipants?.gcReviewers,
    projectParticipants?.designReviewers,
    projectParticipants?.submitterUsers
  ]);

  const getFieldKey = (field: string): string => {
    return field.replace("_custom_fields.", "");
  };

  const fieldsWithValues = useMemo(() => {
    if (!columns || !customFields) return [];

    if (onChange) return columns;

    return columns.filter((col: CustomFieldColumn) => {
      const fieldKey = getFieldKey(col.field);
      const value = customFields[fieldKey];
      return value !== undefined;
    });
  }, [columns, customFields, onChange]);

  const showMessage = async (updateResponse: any) => {
    if (updateResponse.success) {
      message.success("Updated successfully.");
    } else {
      message.error("Failed to update.");
    }
  };

  const [mutationUpdateFeatureInstances] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCES),
    {
      client: gqlClientForProject
    }
  );

  const getFieldLabel = (col: CustomFieldColumn): string => {
    return col.headerName;
  };

  const onUserChange = async (col: CustomFieldColumn, value: any) => {
    const fieldName = col.field
      .replace(/^_custom_fields\./, "")
      .replace(/_obj$/, "");

    if (onChange) {
      onChange(fieldName, value || null);
      return;
    }

    const variables: any = {
      feature_instance_type_id: type,
      feature_instances: [
        {
          id: featureInstanceData.id,
          _custom_fields: {
            [fieldName]: value || null
          }
        }
      ]
    };
    const updateResponse = await mutationUpdateFeatureInstances({
      variables
    });
    showMessage(updateResponse);
  };

  const renderUserField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const userObj = customFields?.[`${fieldKey}`];

    const currentValue = userObj?.id;

    return (
      <Select
        allowClear
        disabled={disabled}
        onChange={(value) => onUserChange(col, value)}
        showSearch
        filterOption
        optionFilterProp="label"
        value={currentValue}
        data-testid={`custom-fields-user-select-${fieldKey}`}
        notFoundContent={
          <SelectSearchNotFoundContent
            notFoundMsg={
              userOptions?.length > 0
                ? "User not found. To add, go to "
                : "No users added to this project. To add, go to "
            }
            linkTitle="Project Settings"
            linkPath="/settings/general/project-companies"
            data-testid={`custom-fields-user-not-found-${fieldKey}`}
          />
        }
      >
        {userOptions?.map((user: any) => {
          const displayName = user.company
            ? // userOptions are built at L92-95 as `{ id, name }` only — no `company`
              // field is ever set, so `user.company` is always undefined and this arm is dead
              /* v8 ignore next */
              `${user.name} (${user.company})`
            : user.name;
          return (
            <Option
              key={user.id}
              value={user.id}
              label={displayName}
              data-testid={`custom-fields-user-option-${fieldKey}`}
            >
              {displayName}
            </Option>
          );
        })}
      </Select>
    );
  };

  const onGeoLocationChange = async (col: any, selection: any) => {
    if (selection) {
      const fieldName = col.field.replace("_custom_fields.", "");

      if (onChange) {
        onChange(fieldName, selection.key || null);
        return;
      }

      const variables: any = {
        feature_instance_type_id: type,
        feature_instances: [
          {
            id: featureInstanceData.id,
            _custom_fields: {
              [fieldName]: selection.key || null
            }
          }
        ]
      };
      const updateResponse = await mutationUpdateFeatureInstances({
        variables
      });
      showMessage(updateResponse);
    }
  };

  const renderGeolocationField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const placeId = customFields?.[fieldKey];
    const geoObj = customFields?.[`${fieldKey}_obj`];

    const currentValueOption =
      placeId && geoObj?.location_service?.label
        ? [
            {
              label: geoObj.location_service.label,
              value: placeId
            }
          ]
        : [];

    const searchResultsWithoutCurrent = shippingLocationOptionResults.filter(
      (opt: { value: string }) => opt.value !== placeId
    );

    let options: { label: string; value: string }[] = [];
    if (searchedString) {
      options = [...currentValueOption, ...searchResultsWithoutCurrent];
    } else if (currentValueOption.length > 0) {
      options = currentValueOption;
    } else {
      options = shippingLocationOptionResults;
    }

    const currentValue =
      placeId && geoObj?.location_service?.label
        ? {
            label: geoObj.location_service.label,
            value: placeId
          }
        : undefined;

    return (
      <Select
        placeholder="Search by Address, City or State"
        allowClear
        disabled={disabled}
        showSearch
        filterOption={false}
        labelInValue
        loading={fetching}
        value={currentValue}
        options={options}
        onSearch={searchLocation}
        onChange={(selection: any) => onGeoLocationChange(col, selection)}
        onClear={() => {
          clearOptions();
        }}
        notFoundContent={
          <LocationSearchNotFoundMessages
            searchedString={searchedString}
            fetching={fetching}
            data-testid="custom-fields-geo-location-not-found"
          />
        }
        data-testid="custom-fields-geo-location-select"
      />
    );
  };

  const onVendorChange = async (col: CustomFieldColumn, value: any) => {
    const fieldName = col.field
      .replace(/^_custom_fields\./, "")
      .replace(/_obj$/, "");

    if (onChange) {
      onChange(fieldName, value || null);
      return;
    }

    const variables: any = {
      feature_instance_type_id: type,
      feature_instances: [
        {
          id: featureInstanceData.id,
          _custom_fields: {
            [fieldName]: value || null
          }
        }
      ]
    };
    const updateResponse = await mutationUpdateFeatureInstances({
      variables
    });
    showMessage(updateResponse);
  };

  const renderVendorField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const vendorObj = customFields?.[`${fieldKey}`];

    const currentValue = vendorObj?.id;

    return (
      <Select
        allowClear
        disabled={disabled}
        onChange={(value) => onVendorChange(col, value)}
        showSearch
        filterOption
        optionFilterProp="label"
        value={currentValue}
        data-testid={`custom-fields-vendor-select-${fieldKey}`}
        notFoundContent={
          <SelectSearchNotFoundContent
            notFoundMsg={
              vendorOptions?.length > 0
                ? "Company not found. To add, go to "
                : "No companies added to this project. To add, go to "
            }
            linkTitle="Project Companies"
            linkPath="/settings/general/project-companies"
            data-testid={`custom-fields-vendor-not-found-${fieldKey}`}
          />
        }
      >
        {vendorOptions?.map((vendor: any) => {
          return (
            <Option
              key={vendor.id}
              value={vendor.id}
              label={vendor.name}
              data-testid={`custom-fields-vendor-option-${fieldKey}`}
            >
              {vendor.name}
            </Option>
          );
        })}
      </Select>
    );
  };

  const onEnumChange = async (col: CustomFieldColumn, value: any) => {
    const fieldName = col.field.replace(/^_custom_fields\./, "");

    if (onChange) {
      onChange(fieldName, value || null);
      return;
    }

    const variables: any = {
      feature_instance_type_id: type,
      feature_instances: [
        {
          id: featureInstanceData.id,
          _custom_fields: {
            [fieldName]: value || null
          }
        }
      ]
    };
    const updateResponse = await mutationUpdateFeatureInstances({
      variables
    });
    showMessage(updateResponse);
  };

  const renderEnumField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const storedValue = customFields?.[fieldKey];
    const options = enumOptionsMap[fieldKey] || [];

    const currentValue = storedValue;

    return (
      <Select
        allowClear
        disabled={disabled}
        onChange={(value) => onEnumChange(col, value)}
        showSearch
        filterOption
        optionFilterProp="label"
        value={currentValue}
        data-testid={`custom-fields-enum-select-${fieldKey}`}
      >
        {options.map((opt: { id: string; value: string }) => (
          <Option
            key={opt.id}
            value={opt.id}
            label={opt.value}
            data-testid={`custom-fields-enum-option-${fieldKey}`}
          >
            {opt.value}
          </Option>
        ))}
      </Select>
    );
  };

  const onDateChange = async (col: CustomFieldColumn, date: any) => {
    const fieldName = col.field.replace(/^_custom_fields\./, "");

    const saveDate = date ? DateUtils.formatDateWithLunchTime(date) : null;

    if (onChange) {
      onChange(fieldName, saveDate);
      return;
    }

    const variables: any = {
      feature_instance_type_id: type,
      feature_instances: [
        {
          id: featureInstanceData.id,
          _custom_fields: {
            [fieldName]: saveDate
          }
        }
      ]
    };
    const updateResponse = await mutationUpdateFeatureInstances({
      variables
    });
    showMessage(updateResponse);
  };

  const renderDateField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const value = customFields[fieldKey];
    const dateValue = value ? dayjs(value) : null;

    return (
      <DatePicker
        value={dateValue}
        placeholder="Select date"
        className="w-full"
        disabled={disabled}
        format={DATE_FORMAT_MMDDYYYY}
        onChange={(date) => onDateChange(col, date)}
        data-testid="custom-fields-date-picker"
      />
    );
  };

  const onTextFieldChange = async (
    col: CustomFieldColumn,
    newValue: string
  ) => {
    const fieldName = col.field.replace(/^_custom_fields\./, "");

    if (onChange) {
      onChange(fieldName, newValue || null);
      return;
    }

    const variables: any = {
      feature_instance_type_id: type,
      feature_instances: [
        {
          id: featureInstanceData.id,
          _custom_fields: {
            [fieldName]: newValue || null
          }
        }
      ]
    };
    const updateResponse = await mutationUpdateFeatureInstances({
      variables
    });
    showMessage(updateResponse);
  };

  const renderTextField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const value = customFields[fieldKey] || "";
    // Bulk-edit mode (onChange provided): report while typing with a stable key
    // so parent state updates do not remount the input and steal focus.
    // Detail-page mode keeps blur-to-save + remount-on-value for server sync.
    const isBulkMode = Boolean(onChange);
    return (
      <Input
        key={isBulkMode ? `text-${fieldKey}` : `text-${fieldKey}-${value}`}
        defaultValue={value}
        disabled={disabled}
        onChange={
          isBulkMode
            ? (event: any) => onTextFieldChange(col, event.target.value)
            : undefined
        }
        onBlur={
          isBulkMode
            ? undefined
            : (event: any) => {
                const newValue = event.target.value;
                if (value === newValue) return;
                onTextFieldChange(col, newValue);
              }
        }
        data-testid="custom-fields-text-input"
      />
    );
  };

  const onNumberChange = async (col: CustomFieldColumn, val: any) => {
    const fieldName = col.field.replace(/^_custom_fields\./, "");

    if (onChange) {
      onChange(fieldName, val.target.value || null);
      return;
    }

    const variables: any = {
      feature_instance_type_id: type,
      feature_instances: [
        {
          id: featureInstanceData.id,
          _custom_fields: {
            [fieldName]: val.target.value || null
          }
        }
      ]
    };
    const updateResponse = await mutationUpdateFeatureInstances({
      variables
    });
    showMessage(updateResponse);
  };

  const renderNumberField = (col: CustomFieldColumn) => {
    const fieldKey = getFieldKey(col.field);
    const value = customFields[fieldKey];
    const isBulkMode = Boolean(onChange);
    return (
      <Input
        key={isBulkMode ? `number-${fieldKey}` : `number-${fieldKey}-${value}`}
        type="number"
        placeholder="Enter Quantity"
        className="w-full"
        disabled={disabled}
        min={0}
        defaultValue={value}
        onKeyDown={(evt) => evt.key === "e" && evt.preventDefault()}
        onChange={
          isBulkMode ? (val: any) => onNumberChange(col, val) : undefined
        }
        onBlur={isBulkMode ? undefined : (val) => onNumberChange(col, val)}
        data-testid="custom-fields-number-input"
      />
    );
  };

  const renderField = (col: CustomFieldColumn) => {
    switch (col.field_type) {
      case "date":
        return renderDateField(col);
      case "user":
        return renderUserField(col);
      case "geolocation":
        return renderGeolocationField(col);
      case "vendor":
        return renderVendorField(col);
      case "number":
        return renderNumberField(col);
      case "enum":
        return renderEnumField(col);
      case "text":
      default:
        return renderTextField(col);
    }
  };

  if (!fieldsWithValues.length) {
    return (
      <div className="mt-3 text-gray-400" data-testid="fmcf-empty">
        No custom fields configured
      </div>
    );
  }

  return (
    <div
      className={className || "py-4 px-6 mt-3 max-h-[600px] overflow-auto"}
      data-testid="fmcf-root"
    >
      <Form layout="vertical">
        {fieldsWithValues.map((col: CustomFieldColumn) => (
          <Form.Item
            key={col.field}
            label={getFieldLabel(col)}
            data-testid={`fmcf-item-${col.field}`}
          >
            {renderField(col)}
          </Form.Item>
        ))}
      </Form>
    </div>
  );
}

export default CustomFields;
